import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { Section, Text } from '@tradingblock/components';

interface ApplicationSubmissionLoaderProps {
  isVisible: boolean;
}

export const ApplicationSubmissionLoader: React.FC<ApplicationSubmissionLoaderProps> = ({ isVisible }) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [currentDot, setCurrentDot] = useState(0);
  const history = useHistory();

  const messages = [
    'Verifying your information with our secure partners...',
    'Processing your application through regulatory systems...',
    'Setting up your trading account infrastructure...',
    'Finalizing security protocols and account validation...',
    'Almost there! Completing your account setup...',
    'Your account is being created with precision and care...',
  ];

  const dots = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];

  // Add estimated time state
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    if (!isVisible) return;

    // Reset elapsed time when starting
    setElapsedTime(0);

    // Rotate through messages every 3 seconds
    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % messages.length);
    }, 3000);

    // Animate dots every 100ms
    const dotInterval = setInterval(() => {
      setCurrentDot(prev => (prev + 1) % dots.length);
    }, 100);

    // Track elapsed time
    const timeInterval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(messageInterval);
      clearInterval(dotInterval);
      clearInterval(timeInterval);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <Section className="application-submission-loader">
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6 text-center">
            <div className="submission-animation-container">
              {/* Main Message */}
              <div className="submission-message">
                <h3 className="mb-3">
                  <Text id="submittingApplication" page="status" />
                </h3>
                <p className="message-text" key={currentMessage}>
                  {messages[currentMessage]}
                </p>
                <div className="loading-dots">
                  <span className="dot">{dots[currentDot]}</span>
                  <span className="dot" style={{ animationDelay: '0.1s' }}>
                    {dots[(currentDot + 1) % dots.length]}
                  </span>
                  <span className="dot" style={{ animationDelay: '0.2s' }}>
                    {dots[(currentDot + 2) % dots.length]}
                  </span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="progress-container mt-4">
                <div className="progress-bar-indeterminate">
                  <div className="progress-track">
                    <div className="progress-fill"></div>
                    <div className="progress-shimmer"></div>
                  </div>
                </div>
                <div className="progress-info mt-2">
                  <p className="progress-text">
                    <Text id="pleaseWait" page="status" />
                  </p>
                  {elapsedTime > 0 && (
                    <p className="elapsed-time">
                      Processing time: {Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')}
                    </p>
                  )}
                </div>
              </div>

              {/* Fun Facts */}
              <div className="fun-facts mt-4">
                <div className="fact-card" style={{ animationDelay: '0.1s' }}>
                  <span className="fact-text">Processing at lightning speed</span>
                </div>
                <div className="fact-card" style={{ animationDelay: '0.2s' }}>
                  <span className="fact-text">Securing your data</span>
                </div>
                <div className="fact-card" style={{ animationDelay: '0.3s' }}>
                  <span className="fact-text">Customizing your experience</span>
                </div>
              </div>

              {/* Cancel option for long waits */}
              {elapsedTime > 120 && (
                <div className="cancel-section mt-4">
                  <p className="cancel-text">
                    This is taking longer than usual. You can safely close this page and check your email for updates.
                  </p>
                  <button
                    className="btn btn-outline-light btn-sm mt-2"
                    onClick={() => history.push('/status')}
                  >
                    Check Status Page
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .application-submission-loader {
          background: var(--blue-dark);
          color: white;
          padding: 0; /* remove outer whitespace */
          margin: 0;
          position: fixed; /* full-viewport overlay */
          inset: 0;
          z-index: 1050;
          width: 100vw;
          height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        /* Remove Bootstrap container/row/col gutters inside loader to avoid side whitespace */
        .application-submission-loader .container-fluid { 
          padding-left: 0; 
          padding-right: 0; 
          height: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
        }
        .application-submission-loader .row { 
          margin-left: 0; 
          margin-right: 0; 
          height: auto;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .application-submission-loader [class^="col-"] { 
          padding-left: 0; 
          padding-right: 0; 
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .application-submission-loader::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, var(--teal) 0%, var(--blue-dark-lighten) 100%);
          opacity: 0.1;
          z-index: 0;
          animation: gradientShift 8s ease infinite;
        }

        @keyframes gradientShift {
          0%, 100% {
            background: linear-gradient(135deg, var(--teal) 0%, var(--blue-dark-lighten) 100%);
          }
          50% {
            background: linear-gradient(135deg, var(--blue-dark-lighten) 0%, var(--teal) 100%);
          }
        }

        .submission-animation-container {
          background: rgba(255, 255, 255, 0.05);
          border-radius: var(--corner-radius);
          padding: calc(var(--gap) * 2) var(--gap);
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          position: relative;
          z-index: 1;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          animation: containerPulse 3s ease-in-out infinite;
          margin: 0 auto;
          width: 100%;
          max-width: 100%;
        }

        @keyframes containerPulse {
          0%, 100% {
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1),
                        0 0 0 0 rgba(255, 255, 255, 0.1);
          }
          50% {
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15),
                        0 0 20px 5px rgba(255, 255, 255, 0.05);
          }
        }

        .submission-message {
          margin-bottom: calc(var(--gap) * 1.5);
        }

        .submission-message h3 {
          color: white;
          font-size: 1.75rem;
          font-weight: 600;
          margin-bottom: var(--gap);
          font-family: 'Poppins', sans-serif;
          animation: titleGlow 2s ease-in-out infinite;
        }

        @keyframes titleGlow {
          0%, 100% {
            text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
          }
          50% {
            text-shadow: 0 0 20px rgba(255, 255, 255, 0.5),
                         0 0 30px rgba(255, 255, 255, 0.3);
          }
        }

        .message-text {
          font-size: 1.1rem;
          margin-bottom: var(--gap);
          min-height: 3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.4;
          animation: messageFade 0.5s ease-in-out;
        }

        @keyframes messageFade {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .loading-dots {
          font-size: 1.5rem;
          color: var(--teal-light);
          margin-top: var(--gap);
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          align-items: center;
        }

        .loading-dots .dot {
          display: inline-block;
        }

        .progress-container {
          max-width: 400px;
          margin: 0 auto;
        }

        .progress-bar-indeterminate {
          width: 100%;
          height: 10px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 5px;
          overflow: hidden;
          position: relative;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .progress-track {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }

        .progress-fill {
          position: absolute;
          top: 0;
          left: -50%;
          width: 50%;
          height: 100%;
          background: linear-gradient(90deg, 
            transparent, 
            rgba(255, 255, 255, 0.4), 
            rgba(255, 255, 255, 0.6),
            rgba(255, 255, 255, 0.4),
            transparent
          );
          animation: progressMove 2s ease-in-out infinite;
          border-radius: 5px;
        }

        .progress-shimmer {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(to right, 
            transparent, 
            rgba(255, 255, 255, 0.2), 
            transparent
          );
          animation: shimmer 1.5s infinite linear;
          border-radius: 5px;
        }

        @keyframes progressMove {
          0% {
            left: -50%;
          }
          100% {
            left: 150%;
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .progress-text {
          font-size: 0.9rem;
          opacity: 0.8;
          color: rgba(255, 255, 255, 0.8);
          margin-top: var(--gap);
        }

        .progress-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .elapsed-time {
          font-size: 0.8rem;
          opacity: 0.7;
          color: rgba(255, 255, 255, 0.7);
        }

        .fun-facts {
          display: flex;
          justify-content: center;
          gap: var(--gap);
          flex-wrap: wrap;
          margin-top: calc(var(--gap) * 1.5);
        }

        .fact-card {
          background: rgba(255, 255, 255, 0.08);
          padding: 0.75rem 1.25rem;
          border-radius: 100px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          animation: fadeInUp 0.6s ease-out both;
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
          backdrop-filter: blur(5px);
          position: relative;
          overflow: hidden;
        }

        .fact-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, 
            transparent, 
            rgba(255, 255, 255, 0.1), 
            transparent
          );
          transition: left 0.5s ease;
        }

        .fact-card:hover::before {
          left: 100%;
        }

        .fact-card:hover {
          background: rgba(255, 255, 255, 0.15);
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2),
                      0 0 15px rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .fact-text {
          white-space: nowrap;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 500;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .application-submission-loader {
            padding: 0; /* keep full-bleed on mobile */
          }

          .submission-animation-container {
            padding: var(--gap);
            margin: var(--gap);
          }

          .submission-message h3 {
            font-size: 1.5rem;
          }

          .message-text {
            font-size: 1rem;
            min-height: 4rem;
          }

          .fun-facts {
            flex-direction: column;
            align-items: center;
            gap: calc(var(--gap) / 2);
          }

          .fact-card {
            width: 100%;
            max-width: 300px;
            justify-content: center;
            padding: 0.5rem 1rem;
          }
        }

        @media (max-width: 576px) {
          .submission-animation-container {
            margin: calc(var(--gap) / 2);
            padding: calc(var(--gap) / 2);
          }

          .submission-message h3 {
            font-size: 1.25rem;
          }

          .message-text {
            font-size: 0.9rem;
          }
        }

        /* Loading animation for the dots */
        .loading-dots .dot {
          animation: dotPulse 1.2s ease-in-out infinite;
        }

        @keyframes dotPulse {
          0%, 100% {
            opacity: 0.4;
            transform: scale(0.8);
          }
          50% {
            opacity: 1;
            transform: scale(1.2);
          }
        }

        .cancel-section {
          margin-top: calc(var(--gap) * 1.5);
          padding: 1rem;
          background: rgba(255, 255, 255, 0.05);
          border-radius: var(--corner-radius);
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .cancel-text {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 0.5rem;
          line-height: 1.5;
        }

        .cancel-section .btn {
          font-size: 0.8rem;
          padding: 0.4rem 0.8rem;
        }
      `}</style>
    </Section>
  );
};
